import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Star, Sparkles, X, Users, ChevronRight, Trophy, Book, Ticket, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const prizeIcons = {
  grand_workshop: Trophy,
  ai_awareness: Sparkles,
  book: Book,
  hr_gala: Ticket,
};

// Scratch Card Component
const ScratchCard = ({ onReveal, isRevealing, language }) => {
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchPercent, setScratchPercent] = useState(0);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#2563eb');
    gradient.addColorStop(1, '#1d4ed8');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(language === 'nl' ? 'KRAS HIER' : 'SCRATCH HERE', canvas.width / 2, canvas.height / 2);
  }, [language]);

  const scratch = (e) => {
    if (isRevealing || hasTriggered) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 25, 0, Math.PI * 2);
    ctx.fill();

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparent = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] === 0) transparent++;
    }
    const percent = (transparent / (imageData.data.length / 4)) * 100;
    setScratchPercent(Math.round(percent));

    if (percent > 50 && !hasTriggered) {
      setHasTriggered(true);
      onReveal();
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-64 h-40 mx-auto cursor-pointer select-none">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center">
          {isRevealing ? (
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
              <p className="mt-2 text-amber-600 font-medium text-sm">
                {language === 'nl' ? 'Resultaat laden...' : 'Loading result...'}
              </p>
            </div>
          ) : (
            <Gift className="w-16 h-16 text-amber-500 animate-pulse" />
          )}
        </div>
        {!isRevealing && (
          <canvas
            ref={canvasRef}
            width={256}
            height={160}
            className="absolute inset-0 rounded-xl touch-none"
            onMouseDown={() => setIsScratching(true)}
            onMouseUp={() => setIsScratching(false)}
            onMouseLeave={() => setIsScratching(false)}
            onMouseMove={(e) => isScratching && scratch(e)}
            onTouchStart={() => setIsScratching(true)}
            onTouchEnd={() => setIsScratching(false)}
            onTouchMove={(e) => scratch(e)}
          />
        )}
      </div>
      {/* Progress indicator */}
      {!isRevealing && !hasTriggered && scratchPercent > 0 && (
        <div className="mt-3 w-64">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{language === 'nl' ? 'Voortgang' : 'Progress'}</span>
            <span>{scratchPercent}% / 50%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-150"
              style={{ width: `${Math.min(scratchPercent * 2, 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1 text-center">
            {language === 'nl' ? 'Kras 50% weg om resultaat te zien' : 'Scratch 50% to reveal result'}
          </p>
        </div>
      )}
    </div>
  );
};

// Treasure Chest Component
const TreasureChest = ({ onReveal, isRevealing, language }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    if (isRevealing) return;
    setIsOpen(true);
    setTimeout(() => onReveal(), 500);
  };

  return (
    <div className="relative w-64 h-48 mx-auto cursor-pointer" onClick={handleClick}>
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center"
        animate={isOpen ? { scale: 1.1 } : { scale: 1 }}
      >
        <div className="relative">
          <motion.div
            className="w-40 h-24 bg-gradient-to-b from-amber-600 to-amber-800 rounded-lg border-4 border-amber-900 relative overflow-hidden"
            animate={isOpen ? { y: 10 } : {}}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-10 bg-amber-400 rounded-full border-2 border-amber-600" />
            <div className="absolute inset-0">
              {[...Array(3)].map((_, i) => (
                <div key={`sep-${i}`} className="w-full h-px bg-amber-900/30 mt-6" />
              ))}
            </div>
          </motion.div>

          <motion.div
            className="absolute -top-8 left-0 w-40 h-12 bg-gradient-to-b from-amber-500 to-amber-700 rounded-t-lg border-4 border-amber-900 origin-bottom"
            animate={isOpen ? { rotateX: -120, y: -20 } : {}}
            transition={{ duration: 0.5 }}
          />

          <AnimatePresence>
            {isOpen && (
              <>
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={`conf-${i}`}
                    className="absolute"
                    initial={{ opacity: 0, y: 0, x: 20 }}
                    animate={{
                      opacity: [0, 1, 0],
                      y: -50 - Math.random() * 50,
                      x: -30 + Math.random() * 60
                    }}
                    transition={{ duration: 1, delay: i * 0.1 }}
                  >
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  </motion.div>
                ))}
              </>
            )}
          </AnimatePresence>
        </div>

        <p className="mt-4 text-amber-700 font-medium">
          {isOpen ? (language === 'nl' ? 'Openen...' : 'Opening...') : (language === 'nl' ? 'Klik om te openen!' : 'Click to open!')}
        </p>
      </motion.div>
    </div>
  );
};

// Slot Machine Component
const SlotMachine = ({ onReveal, isRevealing, language }) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const symbols = ['🎁', '⭐', '💎', '🏆', '📚', '🎫'];

  const handleSpin = () => {
    if (isRevealing || isSpinning) return;
    setIsSpinning(true);
    setTimeout(() => {
      setIsSpinning(false);
      onReveal();
    }, 2000);
  };

  return (
    <div className="flex flex-col items-center">
      <div className="bg-gradient-to-b from-red-600 to-red-800 p-4 rounded-xl border-4 border-yellow-500 shadow-lg">
        <div className="flex gap-2 bg-white rounded-lg p-2">
          {[0, 1, 2].map((col) => (
            <div key={col} className="w-16 h-20 bg-gray-100 rounded overflow-hidden relative">
              <motion.div
                className="flex flex-col items-center"
                animate={isSpinning ? { y: [-200, 0] } : {}}
                transition={{ duration: 0.5, repeat: isSpinning ? 4 : 0, delay: col * 0.2 }}
              >
                {symbols.map((s, i) => (
                  <div key={`sym-${i}`} className="w-16 h-20 flex items-center justify-center text-4xl">
                    {s}
                  </div>
                ))}
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      <motion.button
        className="mt-4 px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-full shadow-lg disabled:opacity-50"
        onClick={handleSpin}
        disabled={isSpinning || isRevealing}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isSpinning ? (language === 'nl' ? 'DRAAIT...' : 'SPINNING...') : 'SPIN!'}
      </motion.button>
    </div>
  );
};

// Main Tombola Modal Component
const TombolaModal = ({ isOpen, onClose, userInfo, assessmentId, forceVisualType = null }) => {
  const { language } = useLanguage();
  const [step, setStep] = useState('loading');
  const [entry, setEntry] = useState(null);
  const [spinResult, setSpinResult] = useState(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [settings, setSettings] = useState(null);
  
  // Test mode: if forceVisualType is set (via URL param or admin), enable test mode
  const isTestMode = !!forceVisualType;
  const requiredReferrals = isTestMode ? 1 : 3;
  
  // Initialize referrals state
  const [referrals, setReferrals] = useState([]);
  const [referralError, setReferralError] = useState('');
  const [isSubmittingReferrals, setIsSubmittingReferrals] = useState(false);
  
  // Reset referrals when modal opens or test mode changes
  useEffect(() => {
    if (isOpen) {
      const count = isTestMode ? 1 : 3;
      setReferrals(Array.from({ length: count }, () => ({ name: '', email: '', company: '' })));
      setReferralError('');
      setStep('loading');
      setSpinResult(null);
    }
  }, [isOpen, isTestMode]);

  const t = {
    title: language === 'nl' ? 'Tombola - Win Prijzen!' : 'Tombola - Win Prizes!',
    loading: language === 'nl' ? 'Laden...' : 'Loading...',
    welcome: language === 'nl' ? 'Welkom bij de Tombola!' : 'Welcome to the Tombola!',
    // Dynamic descriptions per visual type
    scratchDesc: language === 'nl' ? 'Kras de kaart om te ontdekken of je gewonnen hebt!' : 'Scratch the card to discover if you won!',
    chestDesc: language === 'nl' ? 'Klik op de schatkist om te ontdekken of je gewonnen hebt!' : 'Click on the treasure chest to discover if you won!',
    slotDesc: language === 'nl' ? 'Druk op SPIN om te ontdekken of je gewonnen hebt!' : 'Press SPIN to discover if you won!',
    won: language === 'nl' ? 'Gefeliciteerd!' : 'Congratulations!',
    wonDesc: language === 'nl' ? 'Je hebt gewonnen:' : 'You won:',
    notWon: language === 'nl' ? 'Helaas!' : 'Unfortunately!',
    notWonDesc: language === 'nl' ? 'Je hebt deze keer niet gewonnen.' : 'You did not win this time.',
    tryAgain: language === 'nl' ? 'Wil je nog een kans?' : 'Want another chance?',
    tryAgainDesc: language === 'nl' 
      ? `Nodig ${requiredReferrals} collega${requiredReferrals > 1 ? "'s" : ''} uit en krijg een nieuwe poging!` 
      : `Invite ${requiredReferrals} colleague${requiredReferrals > 1 ? 's' : ''} and get another try!`,
    referralTitle: language === 'nl' ? "Nodig collega's uit" : 'Invite colleagues',
    referralDesc: language === 'nl' 
      ? `Vul de gegevens van ${requiredReferrals} collega${requiredReferrals > 1 ? "'s" : ''} in om nog een keer te mogen proberen.` 
      : `Enter the details of ${requiredReferrals} colleague${requiredReferrals > 1 ? 's' : ''} to try again.`,
    name: language === 'nl' ? 'Naam' : 'Name',
    email: 'E-mail',
    company: language === 'nl' ? 'Bedrijf' : 'Company',
    colleague: language === 'nl' ? 'Collega' : 'Colleague',
    submit: language === 'nl' ? 'Verstuur uitnodigingen' : 'Send invitations',
    close: language === 'nl' ? 'Sluiten' : 'Close',
    playAgain: language === 'nl' ? 'Opnieuw spelen' : 'Play again',
    maxReached: language === 'nl' ? 'Je hebt het maximum aantal pogingen (3) bereikt.' : 'You have reached the maximum number of attempts (3).',
    alreadyWon: language === 'nl' ? 'Je hebt al een prijs gewonnen!' : 'You have already won a prize!',
    drawDate: language === 'nl' ? 'Trekking: 15 maart 2026' : 'Draw: March 15, 2026',
    eligibleGrand: language === 'nl' ? 'Je komt in aanmerking voor de hoofdprijs!' : 'You are eligible for the grand prize!',
    notEligibleGrand: language === 'nl' ? 'De hoofdprijs is voor bedrijven >50 medewerkers met HR functies.' : 'The grand prize is for companies >50 employees with HR functions.',
    attempt: language === 'nl' ? 'Poging' : 'Attempt',
    // Win instructions
    prizeShippingInfo: language === 'nl' 
      ? 'Je prijs wordt binnen 5 werkdagen naar je opgestuurd. We nemen contact met je op voor de verzendgegevens.' 
      : 'Your prize will be shipped within 5 business days. We will contact you for shipping details.',
    grandPrizeInfo: language === 'nl'
      ? 'Je maakt nu kans op de hoofdprijs! De trekking vindt plaats op 15 maart 2026. Verhoog je kansen door meer collega\'s uit te nodigen en extra pogingen te doen.'
      : 'You are now in the running for the grand prize! The draw takes place on March 15, 2026. Increase your chances by inviting more colleagues and getting extra attempts.',
    grandPrizeWinInfo: language === 'nl'
      ? 'Geweldig! Je hebt de hoofdprijs gewonnen: een volledige Humanistix AI Workshop ter waarde van €3.750! We nemen binnen 48 uur contact met je op om de workshop in te plannen.'
      : 'Amazing! You won the grand prize: a full Humanistix AI Workshop worth €3,750! We will contact you within 48 hours to schedule the workshop.',
  };

  useEffect(() => {
    if (isOpen && userInfo && assessmentId) {
      initializeEntry();
      fetchSettings();
    }
  }, [isOpen, userInfo, assessmentId]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tombola/status`);
      const data = await res.json();
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch tombola settings:', error);
    }
  };

  const initializeEntry = async () => {
    try {
      // In test mode, generate unique email to allow unlimited plays
      const testEmail = isTestMode 
        ? `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}@test.com`
        : userInfo.email;
        
      const res = await fetch(`${API_URL}/api/tombola/entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: testEmail,
          user_name: userInfo.name || 'Test User',
          user_company: userInfo.company || userInfo.companyName || 'Test Company',
          company_size: userInfo.companySize || userInfo.company_size || '51-200',
          job_title: userInfo.jobTitle || userInfo.job_title || 'HR Manager',
          assessment_id: assessmentId || 'test-assessment'
        })
      });
      const data = await res.json();
      setEntry(data);

      // In test mode, always go to game (ignore limits)
      if (isTestMode) {
        setStep('game');
      } else if (data.spin_count >= 3) {
        setStep('maxReached');
      } else if (data.prizes_won?.length > 0) {
        setStep('alreadyWon');
      } else if (data.needs_referrals) {
        setStep('referrals');
      } else {
        setStep('game');
      }
    } catch (error) {
      console.error('Failed to create tombola entry:', error);
      setStep('game');
    }
  };

  const handleReveal = async () => {
    if (!entry?.entry_id || isRevealing) return;

    setIsRevealing(true);

    try {
      const res = await fetch(`${API_URL}/api/tombola/spin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_id: entry.entry_id })
      });
      const result = await res.json();
      setSpinResult(result);
      setEntry(prev => ({ ...prev, spin_count: result.spin_count }));

      setTimeout(() => {
        setStep('result');
        setIsRevealing(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to spin:', error);
      setIsRevealing(false);
    }
  };

  const handleReferralChange = (index, field, value) => {
    const newReferrals = [...referrals];
    newReferrals[index][field] = value;
    setReferrals(newReferrals);
    setReferralError('');
  };

  const handleSubmitReferrals = async () => {
    // Validate all referrals (dynamic count based on test mode)
    for (let i = 0; i < requiredReferrals; i++) {
      if (!referrals[i].name || !referrals[i].email || !referrals[i].company) {
        setReferralError(language === 'nl' 
          ? `Vul alle velden in voor ${requiredReferrals === 1 ? 'de collega' : `alle ${requiredReferrals} collega's`}` 
          : `Fill in all fields for ${requiredReferrals === 1 ? 'the colleague' : `all ${requiredReferrals} colleagues`}`);
        return;
      }
      if (!referrals[i].email.includes('@')) {
        setReferralError(language === 'nl' ? `Ongeldig e-mailadres voor collega ${i + 1}` : `Invalid email address for colleague ${i + 1}`);
        return;
      }
    }

    setIsSubmittingReferrals(true);

    try {
      // In test mode, skip API call and just go back to game with new entry
      if (isTestMode) {
        // Reset state and create new entry for another play
        setReferrals(Array.from({ length: requiredReferrals }, () => ({ name: '', email: '', company: '' })));
        setSpinResult(null);
        setReferralError('');
        
        // Create new test entry with unique email
        const testEmail = `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}@test.com`;
        
        const res = await fetch(`${API_URL}/api/tombola/entry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_email: testEmail,
            user_name: userInfo?.name || 'Test User',
            user_company: userInfo?.company || userInfo?.companyName || 'Test Company',
            company_size: userInfo?.companySize || userInfo?.company_size || '51-200',
            job_title: userInfo?.jobTitle || userInfo?.job_title || 'HR Manager',
            assessment_id: assessmentId || 'test-assessment'
          })
        });
        const data = await res.json();
        setEntry(data);
        setStep('game');
        setIsSubmittingReferrals(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/tombola/referrals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entry_id: entry.entry_id,
          referrals: referrals
        })
      });

      if (!res.ok) {
        const error = await res.json();
        setReferralError(error.detail || 'Failed to submit referrals');
        return;
      }

      // Reset referrals for next time
      setReferrals(Array.from({ length: requiredReferrals }, () => ({ name: '', email: '', company: '' })));
      setSpinResult(null);
      setStep('game');
    } catch (error) {
      setReferralError(error.message);
    } finally {
      setIsSubmittingReferrals(false);
    }
  };

  const renderGame = () => {
    const visualType = forceVisualType || settings?.visual_type || 'scratch_card';
    
    // Get the appropriate description for the visual type
    const getDescription = () => {
      switch (visualType) {
        case 'scratch_card': return t.scratchDesc;
        case 'treasure_chest': return t.chestDesc;
        case 'slot_machine': return t.slotDesc;
        default: return t.scratchDesc;
      }
    };

    return (
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-800 mb-2">{t.welcome}</h3>
        <p className="text-gray-600 mb-6">{getDescription()}</p>

        {entry?.eligible_for_grand_prize ? (
          <div className="mb-4 p-3 bg-green-100 rounded-lg text-green-700 text-sm">
            <Trophy className="inline w-4 h-4 mr-2" />
            {t.eligibleGrand}
          </div>
        ) : (
          <div className="mb-4 p-3 bg-amber-50 rounded-lg text-amber-700 text-sm">
            {t.notEligibleGrand}
          </div>
        )}

        <div className="my-8">
          {visualType === 'scratch_card' && (
            <ScratchCard onReveal={handleReveal} isRevealing={isRevealing} language={language} />
          )}
          {visualType === 'treasure_chest' && (
            <TreasureChest onReveal={handleReveal} isRevealing={isRevealing} language={language} />
          )}
          {visualType === 'slot_machine' && (
            <SlotMachine onReveal={handleReveal} isRevealing={isRevealing} language={language} />
          )}
        </div>

        <p className="text-xs text-gray-500 mt-4">
          {t.drawDate} - {t.attempt} {entry?.spin_count || 0}/3
        </p>
      </div>
    );
  };

  const renderResult = () => {
    const won = spinResult?.won;
    const prize = spinResult?.prize;
    const isGrandPrize = prize?.is_grand_prize;
    const PrizeIcon = prize ? (prizeIcons[prize.name] || Gift) : Gift;

    // Function to play again in test mode
    const handlePlayAgain = () => {
      setSpinResult(null);
      initializeEntry();
    };

    return (
      <div className="text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.5 }}
        >
          {won ? (
            <>
              <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center">
                <PrizeIcon className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-green-600 mb-2">{t.won}</h3>
              <p className="text-gray-600 mb-4">{t.wonDesc}</p>
              <div className="p-4 bg-gradient-to-br from-amber-50 to-blue-50 rounded-xl border-2 border-amber-200">
                <p className="text-xl font-bold text-amber-800">{prize?.name}</p>
                {prize?.value > 0 && (
                  <p className="text-amber-600">t.w.v. €{prize.value}</p>
                )}
                <p className="text-sm text-gray-600 mt-2">{prize?.description}</p>
              </div>
              
              {/* Win instructions based on prize type */}
              <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200 text-left">
                <h4 className="font-bold text-green-800 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  {language === 'nl' ? 'Wat gebeurt er nu?' : 'What happens next?'}
                </h4>
                <p className="text-sm text-green-700">
                  {isGrandPrize ? t.grandPrizeWinInfo : t.prizeShippingInfo}
                </p>
              </div>

              {/* If not grand prize but eligible, show grand prize info */}
              {!isGrandPrize && entry?.eligible_for_grand_prize && (
                <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200 text-left">
                  <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                    <Trophy className="w-5 h-5" />
                    {language === 'nl' ? 'Hoofdprijs Trekking' : 'Grand Prize Draw'}
                  </h4>
                  <p className="text-sm text-amber-700">{t.grandPrizeInfo}</p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                <Gift className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">{t.notWon}</h3>
              <p className="text-gray-600 mb-4">{t.notWonDesc}</p>

              {(spinResult?.can_spin_again || isTestMode) && (
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <h4 className="font-bold text-blue-800 mb-2">{t.tryAgain}</h4>
                  <p className="text-sm text-blue-600 mb-4">{t.tryAgainDesc}</p>
                  <button
                    onClick={() => setStep('referrals')}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Users className="inline w-4 h-4 mr-2" />
                    {t.referralTitle}
                  </button>
                </div>
              )}
            </>
          )}
        </motion.div>

        <div className="mt-6 flex gap-3 justify-center">
          {isTestMode && (
            <button
              onClick={handlePlayAgain}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
            >
              {t.playAgain}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            {t.close}
          </button>
        </div>
      </div>
    );
  };

  const renderReferrals = () => (
    <div>
      <h3 className="text-xl font-bold text-gray-800 mb-2">{t.referralTitle}</h3>
      <p className="text-gray-600 mb-6">{t.referralDesc}</p>

      <div className="space-y-4">
        {referrals.map((_, index) => (
          <div key={`ref-${index}`} className="p-4 bg-gray-50 rounded-xl">
            <p className="font-medium text-gray-700 mb-3">{t.colleague} {index + 1}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500">{t.name}</label>
                <input
                  type="text"
                  value={referrals[index].name}
                  onChange={(e) => handleReferralChange(index, 'name', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Jan Jansen"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">{t.email}</label>
                <input
                  type="email"
                  value={referrals[index].email}
                  onChange={(e) => handleReferralChange(index, 'email', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="jan@bedrijf.nl"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">{t.company}</label>
                <input
                  type="text"
                  value={referrals[index].company}
                  onChange={(e) => handleReferralChange(index, 'company', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Bedrijf B.V."
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {referralError && (
        <p className="mt-4 text-red-600 text-sm">{referralError}</p>
      )}

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleSubmitReferrals}
          disabled={isSubmittingReferrals}
          className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {isSubmittingReferrals ? '...' : t.submit}
          <ChevronRight className="inline w-4 h-4 ml-2" />
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
        >
          {t.close}
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        data-testid="tombola-modal-backdrop"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          data-testid="tombola-modal"
        >
          <div className="bg-gradient-to-r from-blue-500 to-amber-500 p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">{t.title}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
              data-testid="tombola-close-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          <div className="p-6">
            {step === 'loading' && (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="mt-4 text-gray-600">{t.loading}</p>
              </div>
            )}
            {step === 'game' && renderGame()}
            {step === 'result' && renderResult()}
            {step === 'referrals' && renderReferrals()}
            {step === 'maxReached' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <Gift className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-600">{t.maxReached}</p>
                <button onClick={onClose} className="mt-4 text-blue-600 hover:underline">
                  {t.close}
                </button>
              </div>
            )}
            {step === 'alreadyWon' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                  <Trophy className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-gray-600">{t.alreadyWon}</p>
                <button onClick={onClose} className="mt-4 text-blue-600 hover:underline">
                  {t.close}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TombolaModal;
